import './list-filters.css'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'

import { Form, SubmitButton } from 'ui/form'
import { Button } from 'ui/button'

import { useHistory, useLocation } from 'router'
import { toSearchParamsString } from 'utils/url'
import { cn } from 'utils'
import { softCompare } from 'utils/object'
import { listFilters, listFiltersPermanent } from 'api/storage'
import { DEFAULT_LIMIT } from 'const/list'

import { useListController } from './list-context'
import { FilterExpand } from './filter-expand'

export const getFilledParamsFromUrl = (search) => {
  const searchParams = new URLSearchParams(search)
  const params = {}

  for (let key of searchParams.keys()) {
    if (key.slice(-2) === '[]') {
      //Remove square brackets from name
      params[key.slice(0, -2)] = searchParams.getAll(key)
    } else {
      params[key] = searchParams.get(key)
    }
  }
  return params
}

export const ListFilters = ({
  filters,
  updateLocation,
  format,
  parse,
  page,
  limit,
  className,
  filterName,
  namePrefix,
  isOpen,
  permanent,
  children,
}) => {
  const prevSearch = useRef(null)
  const form = useRef(null)
  const location = useLocation()
  const history = useHistory()
  const { setFilters, refresh } = useListController()

  const searchValues = useMemo(() => {
    const { page, limit, ...filters } = getFilledParamsFromUrl(location.search)
    return filters
  }, [location.search])

  useEffect(() => {
    if (updateLocation) {
      const hasValues = Object.entries(searchValues)?.length
      const notEmptyFilters = Object.fromEntries(
        Object.entries(searchValues).filter(([, value]) =>
          Array.isArray(value) ? value?.length : value,
        ),
      )
      hasValues && setFilters(notEmptyFilters)
      filters && !hasValues && prevSearch?.current && setFilters({})
    }
  }, [location.search]) //eslint-disable-line

  useEffect(() => {
    if (updateLocation) {
      const savedFilters = permanent
        ? listFiltersPermanent.value?.[filterName]
        : listFilters.value?.[filterName]
      const search = getFilledParamsFromUrl(location.search)
      const hasValues = Object.entries(search)?.length
      if (!hasValues && savedFilters) {
        const searchParams = toSearchParamsString(savedFilters)
        history.replace({
          ...location,
          search: searchParams,
        })
        prevSearch.current = searchParams
      }
    }
  }, []) //eslint-disable-line

  useEffect(() => {
    if (filterName) {
      const storageFilters = permanent ? listFiltersPermanent.value ?? {} : listFilters.value ?? {}
      storageFilters[filterName] = { ...storageFilters?.[filterName], page, limit }
      if (permanent) {
        listFiltersPermanent.value = storageFilters
      } else {
        listFilters.value = storageFilters
      }
    }
  }, [page, limit, filterName, permanent])

  const addFilters = useCallback(
    (/*currentFilters*/) => {
      if (!form?.current) return
      const currentFilters = form.current.getValues()

      let notEmptyFilters = Object.fromEntries(
        Object.entries(currentFilters).filter(([, value]) =>
          Array.isArray(value) ? value?.length : value,
        ),
      )

      if (filterName) {
        const storageFilters = permanent
          ? listFiltersPermanent?.value ?? {}
          : listFilters.value ?? {}
        storageFilters[filterName] = { ...notEmptyFilters, page, limit }
        if (permanent) {
          listFiltersPermanent.value = storageFilters
        } else {
          listFilters.value = storageFilters
        }
      }

      if (softCompare({ ...filters }, { ...notEmptyFilters })) {
        refresh()
      }

      if (format) {
        notEmptyFilters = format(notEmptyFilters)
      }

      if (updateLocation) {
        const searchParams = toSearchParamsString(notEmptyFilters)
        history.replace({
          ...location,
          search: searchParams,
        })
        prevSearch.current = searchParams
      } else {
        Object.values(notEmptyFilters)?.length && setFilters(notEmptyFilters)
      }
    },
    [
      filterName,
      filters,
      format,
      updateLocation,
      setFilters,
      permanent,
      page,
      limit,
      refresh,
      history,
      location,
    ],
  )

  const defaultValues = useMemo(() => {
    const storageFilters = permanent
      ? listFiltersPermanent.value?.[filterName] ?? {}
      : listFilters.value?.[filterName] ?? {}
    const currentFilters = updateLocation ? getFilledParamsFromUrl(location.search) : filters
    const parsedFilters = parse ? parse(currentFilters) : currentFilters
    return Object.values(parsedFilters).length ? parsedFilters : storageFilters
  }, [filterName, filters, location.search, parse, permanent, updateLocation])

  const handleReset = () => {
    if (permanent ? listFiltersPermanent.value?.[filterName] : listFilters.value?.[filterName]) {
      const storageFilters = permanent ? listFiltersPermanent.value ?? {} : listFilters.value ?? {}
      const { [filterName]: redundant, ...otherStorageFilters } = storageFilters
      if (permanent) {
        listFiltersPermanent.value = otherStorageFilters
      } else {
        listFilters.value = otherStorageFilters
      }
    }
    if (updateLocation) {
      const searchParams = toSearchParamsString({})
      history.replace({
        ...location,
        search: searchParams,
      })
      prevSearch.current = searchParams
    }
    setFilters({})
    form?.current?.reset()
  }

  const footer = () => (
    <footer>
      <Button theme="secondary" onClick={handleReset}>
        Clear Filters
      </Button>
      <SubmitButton>Search</SubmitButton>
    </footer>
  )

  return (
    <Form
      name={namePrefix && `${namePrefix}_list_filters_form`}
      defaultValues={defaultValues}
      className={cn('list-filters', className)}
      onSubmit={addFilters}
      ref={form}
    >
      <FilterExpand isOpen={isOpen} Footer={footer}>
        {children}
      </FilterExpand>
    </Form>
  )
}

ListFilters.displayName = 'List Filters'

ListFilters.propTypes = {
  filterName: PropTypes.string,
  filters: PropTypes.object,
  format: PropTypes.func,
  isOpen: PropTypes.bool,
  limit: PropTypes.number,
  namePrefix: PropTypes.string,
  page: PropTypes.number,
  parse: PropTypes.func,
  permanent: PropTypes.bool,
  updateLocation: PropTypes.bool,
}

ListFilters.defaultProps = {
  page: 1,
  limit: DEFAULT_LIMIT,
  isOpen: false,
}
